/**
 * Display utilities for formatting and detecting value types
 * Reusable across CRM, web, and other apps
 */

/** Value types that can be detected and rendered specially */
export type ValueType = 'url' | 'email' | 'phone' | 'text';

/** URL patterns for detection */
const URL_REGEX = /^(https?:\/\/|www\.)[^\s]+$/i;
const URL_FIELD_SUFFIXES = ['_url', '_link', '_website', '_domain'];

/** Email pattern */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Phone pattern - matches various formats */
const PHONE_REGEX = /^[\d\s\-+().]{7,20}$/;

/**
 * Convert snake_case or kebab-case to Title Case
 * @example formatLabel('employee_count_range') → 'Employee Count Range'
 * @example formatLabel('linkedin-url') → 'Linkedin Url'
 */
export function formatLabel(input: string): string {
  if (!input) return '';
  return input
    .split(/[_-]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Detect the type of value based on field name and value content
 * Uses hybrid approach: check field name first, then regex fallback
 */
export function detectValueType(name: string, value: string): ValueType {
  if (!value || typeof value !== 'string') return 'text';

  const lowerName = name.toLowerCase();

  // Check field name hints first
  if (URL_FIELD_SUFFIXES.some((suffix) => lowerName.endsWith(suffix))) {
    return 'url';
  }
  if (lowerName.includes('email')) {
    return 'email';
  }
  if (lowerName.includes('phone') || lowerName.includes('mobile') || lowerName.includes('fax')) {
    return 'phone';
  }

  // Fallback to regex detection on value
  if (URL_REGEX.test(value)) return 'url';
  if (EMAIL_REGEX.test(value)) return 'email';
  if (PHONE_REGEX.test(value) && /\d/.test(value)) return 'phone';

  return 'text';
}

/**
 * Format phone number for display
 * Preserves original format but ensures consistency
 */
export function formatPhoneDisplay(phone: string): string {
  // Already formatted, return as-is
  if (!phone) return '';
  return phone.trim();
}

/**
 * Get href for a value based on its type
 */
export function getValueHref(value: string, type: ValueType): string | null {
  switch (type) {
    case 'url':
      // Add protocol if missing
      if (value.startsWith('www.')) return `https://${value}`;
      return value;
    case 'email':
      return `mailto:${value}`;
    case 'phone':
      // Strip non-numeric except + for international
      const cleaned = value.replace(/[^\d+]/g, '');
      return `tel:${cleaned}`;
    default:
      return null;
  }
}

/**
 * Check if a value is considered "empty" or invalid
 */
export function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase();
    const emptyValues = ['', 'null', 'not available', 'n/a', 'na', 'unknown', 'undefined', 'none'];
    return emptyValues.includes(trimmed);
  }
  return false;
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}
